import { ChevronDownIcon } from '@radix-ui/react-icons';
import type { EbookPageResult, EbookRecognitionMode } from '@wx-weiqi/contracts';
import './ebook-optimization.css';

export const ebookRecognitionLabels: Record<EbookRecognitionMode, string> = {
  AUTO: '自动优化',
  ORIGINAL: '原图识别',
  FAINT: '淡印增强',
  BLEED: '透印抑制',
};

export function ebookOptimizationSummary(result: EbookPageResult) {
  const optimization = result.optimization;
  if (!optimization) return '';
  const reasons = {
    GRID_MISMATCH: '与原题图的位置或格线无法对应',
    STONE_CONFLICT: '原有可靠棋子的颜色、位置或清晰度发生变化',
    SEPARATE_BOARDS: '将独立题图合并在了一起',
    WEAK_CANDIDATE: '新增题图的棋子证据不足',
    NO_IMPROVEMENT: '题图完整性及疑点未改善',
  };
  const rejected = optimization.compared.filter(
    (comparison) =>
      comparison.rejection &&
      (comparison.rejection !== 'NO_IMPROVEMENT' || optimization.appliedMode === 'ORIGINAL'),
  );
  const details = rejected.map(
    (comparison) =>
      `${ebookRecognitionLabels[comparison.mode]}检出 ${comparison.candidates} 个题图，但${reasons[comparison.rejection!]}，未采用。`,
  );
  if (optimization.reason === 'RETAINED_ORIGINAL' && !rejected.length)
    details.push('本次优化未采用，已保留原图识别结果。');
  if (optimization.reason === 'PROCESSING_BUDGET')
    details.push('已达到本页处理预算，保留已完成比较的结果。');
  if (result.candidates.some((candidate) => candidate.completeness?.clippedSides.length))
    details.push('部分题框外仍有格线，题图可能不完整，请对照原图扩大补框范围。');
  return `本次采用：${ebookRecognitionLabels[optimization.appliedMode]}，识别出 ${result.candidates.length} 个题图。${details.join('')}切换查看只改变预览，不会更换识别结果；无红点也需检查题图是否完整，红点仍需校对。`;
}

export function EbookOptimizationControl({
  disabled,
  pending,
  label,
  onChoose,
}: {
  disabled: boolean;
  pending: boolean;
  label: string;
  onChoose: (mode: EbookRecognitionMode) => void;
}) {
  return (
    <span className="ebook-optimization-control">
      <button
        type="button"
        disabled={disabled}
        aria-busy={pending}
        title="比较原图与优化结果，保留更可靠的识别；原 PDF 和已校对题目保留"
        onClick={() => onChoose('AUTO')}
      >
        {label}
      </button>
      <span className="ebook-optimization-picker">
        <select
          aria-label="选择识别处理方式"
          disabled={disabled}
          value=""
          onChange={(event) => onChoose(event.target.value as EbookRecognitionMode)}
        >
          <option value="" disabled>
            选择处理方式
          </option>
          <option value="AUTO">自动优化（默认）</option>
          <option value="FAINT">淡印增强：适合字迹、格线偏淡</option>
          <option value="BLEED">透印抑制：适合背面棋子阴影</option>
          <option value="ORIGINAL">按原图重新识别</option>
        </select>
        <ChevronDownIcon aria-hidden="true" />
      </span>
    </span>
  );
}
